/**
 * Depth-agnostic helpers for the business segment, customer account and
 * operating location filters.
 *
 * Segment and customer are flat lists — a list is just a tree of depth one, so
 * they share every helper below with the four-level location tree.
 *
 * Node shape (uniform at every depth):
 *   { key, name, children?: Node[], leafId?: string | number }
 *
 * Selection is stored as leaf ids only. Every parent checkbox state is derived
 * from its descendant leaves, so no category / state / city id is ever persisted.
 */

// Depot names are only unique within a city, so depot leaf ids are composite.
const LEAF_ID_SEPARATOR = "\u0001";

export const makeDepotLeafId = (country, state, city, depot) =>
  [country, state, city, depot].join(LEAF_ID_SEPARATOR);

/** A fleet with no category still needs a segment to sit under. */
export const UNCATEGORIZED_SEGMENT = "Uncategorized";

const segmentOf = (fleet) => fleet.category || UNCATEGORIZED_SEGMENT;

/**
 * Business segment options: one node per distinct fleet category, in the order
 * the fleets API returns them. Nothing about the category list is hardcoded.
 */
export const buildSegmentNodes = (allFleets = []) => {
  const seen = new Set();
  const nodes = [];
  (allFleets || []).forEach((fleet) => {
    const segment = segmentOf(fleet);
    if (seen.has(segment)) return;
    seen.add(segment);
    nodes.push({ key: `segment${LEAF_ID_SEPARATOR}${segment}`, name: segment, leafId: segment });
  });
  return nodes;
};

/**
 * Customer account options, scoped to the applied segments. Passing null means
 * unscoped. Fleets are de-duplicated by id, since the fleets API can list the
 * same id under more than one category.
 */
export const buildCustomerNodes = (allFleets = [], segments = null) => {
  const scope = segments === null ? null : new Set(segments);
  const seen = new Set();
  const nodes = [];
  (allFleets || []).forEach((fleet) => {
    if (scope && !scope.has(segmentOf(fleet))) return;
    if (seen.has(fleet.id)) return;
    seen.add(fleet.id);
    nodes.push({ key: `fleet${LEAF_ID_SEPARATOR}${fleet.id}`, name: fleet.name, leafId: fleet.id });
  });
  return nodes;
};

/** Every fleet id inside the given segments. */
export const fleetIdsForSegments = (segments, allFleets = []) =>
  collectAllLeafIds(buildCustomerNodes(allFleets, segments || []));

/**
 * Order-insensitive identity of a fleet selection.
 *
 * The panel emits its draft Set in click order, while the pages re-derive the
 * list in `allFleets` order before echoing it back, so the same selection
 * returns permuted. Anything comparing "what we applied" against "what came
 * back" has to normalise, or it reads a reorder as a new selection.
 */
export const fleetSelectionKey = (fleetIds) =>
  [...(fleetIds || [])].map(String).sort().join(",");

/**
 * Which segments an applied fleet selection implies: every segment with at
 * least one of its fleets selected. Only used to seed the segment panel from a
 * selection that came from outside the control — a page reset or a deep link.
 */
export const deriveSegmentsFromFleetIds = (fleetIds, allFleets = []) => {
  const selected = new Set(fleetIds || []);
  return buildSegmentNodes((allFleets || []).filter((fleet) => selected.has(fleet.id))).map(
    (node) => node.leafId
  );
};

/**
 * Location tree: country -> state -> city -> depot.
 *
 * Cities with no depots are dropped, along with any state or country left empty
 * by that pruning: `excludeNotDefinedOriginDepot` is sent unconditionally on
 * every request, so a depot-less city can never contribute rows and would only
 * render as a permanently unselectable row.
 */
export const buildLocationTree = (hierarchy = []) => {
  const countries = [];
  (hierarchy || []).forEach((country) => {
    const states = [];
    (country.states || []).forEach((state) => {
      const cities = [];
      (state.cities || []).forEach((city) => {
        const depots = (city.depots || []).map((depot) => ({
          key: makeDepotLeafId(country.country, state.state, city.name, depot),
          name: depot,
          leafId: makeDepotLeafId(country.country, state.state, city.name, depot),
        }));
        if (depots.length === 0) return;
        cities.push({
          key: `city${LEAF_ID_SEPARATOR}${country.country}${LEAF_ID_SEPARATOR}${state.state}${LEAF_ID_SEPARATOR}${city.name}`,
          name: city.name,
          children: depots,
        });
      });
      if (cities.length === 0) return;
      states.push({
        key: `state${LEAF_ID_SEPARATOR}${country.country}${LEAF_ID_SEPARATOR}${state.state}`,
        name: state.state,
        children: cities,
      });
    });
    if (states.length === 0) return;
    countries.push({
      key: `country${LEAF_ID_SEPARATOR}${country.country}`,
      name: country.country,
      children: states,
    });
  });
  return countries;
};

/** Map of node key -> every leaf id beneath it. Computed once per tree. */
export const buildLeafIndex = (nodes = []) => {
  const index = new Map();
  const walk = (node) => {
    if (!node.children || node.children.length === 0) {
      const leaves = node.leafId === undefined ? [] : [node.leafId];
      index.set(node.key, leaves);
      return leaves;
    }
    const leaves = [];
    node.children.forEach((child) => leaves.push(...walk(child)));
    index.set(node.key, leaves);
    return leaves;
  };
  nodes.forEach(walk);
  return index;
};

export const collectAllLeafIds = (nodes = []) => {
  const leaves = [];
  const walk = (node) => {
    if (!node.children || node.children.length === 0) {
      if (node.leafId !== undefined) leaves.push(node.leafId);
      return;
    }
    node.children.forEach(walk);
  };
  nodes.forEach(walk);
  return leaves;
};

export const NODE_STATE = {
  CHECKED: "checked",
  INDETERMINATE: "indeterminate",
  UNCHECKED: "unchecked",
};

/** Parent state is derived, never stored. */
export const getNodeState = (nodeKey, leafIndex, selected) => {
  const leaves = leafIndex.get(nodeKey) || [];
  if (leaves.length === 0) return NODE_STATE.UNCHECKED;
  let selectedCount = 0;
  leaves.forEach((leafId) => {
    if (selected.has(leafId)) selectedCount += 1;
  });
  if (selectedCount === 0) return NODE_STATE.UNCHECKED;
  if (selectedCount === leaves.length) return NODE_STATE.CHECKED;
  return NODE_STATE.INDETERMINATE;
};

/**
 * Toggling a node applies to every leaf beneath it. Only a fully selected node
 * clears, so clicking an indeterminate parent selects all of its leaves.
 */
export const toggleNode = (nodeKey, leafIndex, selected) => {
  const leaves = leafIndex.get(nodeKey) || [];
  const next = new Set(selected);
  const allSelected = leaves.length > 0 && leaves.every((leafId) => next.has(leafId));
  if (allSelected) leaves.forEach((leafId) => next.delete(leafId));
  else leaves.forEach((leafId) => next.add(leafId));
  return next;
};

/**
 * Search at every level.
 *   - a node whose own name matches keeps its full subtree
 *   - a matching descendant keeps the pruned path and expands every ancestor
 * Returns the filtered nodes plus the keys to auto-expand.
 */
export const filterTree = (nodes = [], query = "") => {
  const trimmed = (query || "").trim().toLowerCase();
  if (!trimmed) return { nodes, expandedKeys: {} };

  const expandedKeys = {};

  const walk = (node) => {
    const selfMatch = (node.name || "").toLowerCase().includes(trimmed);
    if (!node.children || node.children.length === 0) {
      return selfMatch ? node : null;
    }
    if (selfMatch) return node;

    const children = node.children.map(walk).filter(Boolean);
    if (children.length === 0) return null;
    expandedKeys[node.key] = true;
    return { ...node, children };
  };

  return { nodes: nodes.map(walk).filter(Boolean), expandedKeys };
};

/**
 * Rebuild the `originGroupedLocations` payload the API already expects, keeping
 * the existing collapsing: a fully selected level is emitted without children.
 * Callers send [] instead when every depot is selected, which the API layer
 * reads as "no location filter".
 */
export const leafIdsToHierarchicalLocation = (selectedLeafIds, nodes, leafIndex) => {
  const selected = selectedLeafIds instanceof Set ? selectedLeafIds : new Set(selectedLeafIds || []);
  const index = leafIndex || buildLeafIndex(nodes);

  const result = [];
  (nodes || []).forEach((country) => {
    const countryState = getNodeState(country.key, index, selected);
    if (countryState === NODE_STATE.UNCHECKED) return;
    if (countryState === NODE_STATE.CHECKED) {
      result.push({ country: country.name });
      return;
    }

    const countryNode = { country: country.name, children: [] };
    (country.children || []).forEach((state) => {
      const stateState = getNodeState(state.key, index, selected);
      if (stateState === NODE_STATE.UNCHECKED) return;
      if (stateState === NODE_STATE.CHECKED) {
        countryNode.children.push({ state: state.name });
        return;
      }

      const stateNode = { state: state.name, children: [] };
      (state.children || []).forEach((city) => {
        const cityState = getNodeState(city.key, index, selected);
        if (cityState === NODE_STATE.UNCHECKED) return;
        if (cityState === NODE_STATE.CHECKED) {
          stateNode.children.push({ city: city.name });
          return;
        }

        const cityNode = { city: city.name, children: [] };
        (city.children || []).forEach((depot) => {
          if (selected.has(depot.leafId)) cityNode.children.push({ depot: depot.name });
        });
        stateNode.children.push(cityNode);
      });
      countryNode.children.push(stateNode);
    });
    result.push(countryNode);
  });

  return result;
};

/**
 * Seed a draft from the applied `originGroupedLocations` value.
 * null / [] means the filter is unset, i.e. every depot is selected.
 */
export const hierarchicalLocationToLeafIds = (applied, nodes) => {
  if (!applied || applied.length === 0) return collectAllLeafIds(nodes);

  const leafIds = [];
  applied.forEach((appliedCountry) => {
    const country = (nodes || []).find((c) => c.name === appliedCountry.country);
    if (!country) return;
    if (!appliedCountry.children || appliedCountry.children.length === 0) {
      leafIds.push(...collectAllLeafIds([country]));
      return;
    }

    appliedCountry.children.forEach((appliedState) => {
      const state = (country.children || []).find((s) => s.name === appliedState.state);
      if (!state) return;
      if (!appliedState.children || appliedState.children.length === 0) {
        leafIds.push(...collectAllLeafIds([state]));
        return;
      }

      appliedState.children.forEach((appliedCity) => {
        const city = (state.children || []).find((c) => c.name === appliedCity.city);
        if (!city) return;
        if (!appliedCity.children || appliedCity.children.length === 0) {
          leafIds.push(...collectAllLeafIds([city]));
          return;
        }

        appliedCity.children.forEach((appliedDepot) => {
          const depot = (city.children || []).find((d) => d.name === appliedDepot.depot);
          if (depot) leafIds.push(depot.leafId);
        });
      });
    });
  });

  return leafIds;
};

/**
 * Trigger summary: all selected -> allLabel, one -> that name,
 * many -> first name + N. Also returns the full list for the tooltip.
 */
export const getSelectionSummary = (selectedLeafIds, nodes, allLabel) => {
  const selected = selectedLeafIds instanceof Set ? selectedLeafIds : new Set(selectedLeafIds || []);
  const names = [];
  const walk = (node) => {
    if (!node.children || node.children.length === 0) {
      if (node.leafId !== undefined && selected.has(node.leafId)) names.push(node.name);
      return;
    }
    node.children.forEach(walk);
  };
  (nodes || []).forEach(walk);

  const totalLeaves = countLeaves(nodes);
  if (totalLeaves === 0 || names.length === totalLeaves) {
    return { label: allLabel, tooltip: allLabel };
  }
  if (names.length === 0) return { label: allLabel, tooltip: allLabel };
  return {
    label: names.length === 1 ? names[0] : `${names[0]} + ${names.length - 1}`,
    tooltip: names.join(", "),
  };
};

/** Distinct leaf count. Guards against duplicate ids from the fleets API. */
export const countLeaves = (nodes = []) => new Set(collectAllLeafIds(nodes)).size;

/**
 * The name of the only leaf a dimension offers, or null when it offers zero or
 * more than one.
 *
 * A dimension with a single option cannot filter anything, so it renders as a
 * static field rather than a dropdown - this is the value that field shows.
 * Leaves are deduplicated by leafId exactly as countLeaves does, so the two
 * always agree about what "one option" means; a name is taken from the first
 * node carrying that leafId.
 */
export const soleLeafName = (nodes = []) => {
  const nameByLeafId = new Map();
  const walk = (node) => {
    if (!node.children || node.children.length === 0) {
      if (node.leafId !== undefined && !nameByLeafId.has(node.leafId)) {
        nameByLeafId.set(node.leafId, node.name);
      }
      return;
    }
    node.children.forEach(walk);
  };
  (nodes || []).forEach(walk);
  return nameByLeafId.size === 1 ? [...nameByLeafId.values()][0] : null;
};

/** True when every leaf in the tree is selected. */
export const isEverythingSelected = (selectedLeafIds, nodes) => {
  const total = countLeaves(nodes);
  if (total === 0) return true;
  const selected = selectedLeafIds instanceof Set ? selectedLeafIds : new Set(selectedLeafIds || []);
  return collectAllLeafIds(nodes).every((leafId) => selected.has(leafId));
};

/**
 * True when an applied location payload names nothing that still exists in the
 * tree. That is different from an unset payload: the filter is live on the API
 * but has nothing the panel can show, so the caller has to resolve the desync
 * rather than render it as "everything selected".
 */
export const isAppliedLocationStale = (applied, nodes) => {
  if (!applied || applied.length === 0) return false;
  return hierarchicalLocationToLeafIds(applied, nodes).length === 0;
};

/**
 * Expansion state the panels open with: every root node expanded, so the first
 * level under it is visible without a click. Leaf-only roots are left out.
 */
export const buildRootExpandedKeys = (nodes = []) => {
  const expanded = {};
  (nodes || []).forEach((node) => {
    if (node.children && node.children.length) expanded[node.key] = true;
  });
  return expanded;
};
